import React, { useMemo } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ProductColumn, ProductRenderParamsType, ProductType } from '../../../../types/shipment';
import { Typography, IconButton, Box, useTheme, alpha } from '@mui/material';
import { isEUCountry } from '../../../../utils/euCompliance';
import { isUSDestination, normalizeCpscDisclaimCode } from '../../../../utils/usCompliance';

const BASE_COLUMNS: ProductColumn[] = [
  { id: 'description', label: 'Description', minWidth: 160, align: 'left' },
  { id: 'quantity', label: 'Qty', minWidth: 60, align: 'right' },
  {
    id: 'unitValue',
    label: 'Unit Value',
    minWidth: 95,
    align: 'right',
  },
  {
    id: 'HSNName',
    label: 'HSN Name',
    minWidth: 130,
    align: 'left',
  },
  {
    id: 'HSNCode',
    label: 'HSN Code',
    minWidth: 110,
    align: 'left',
  },
];

const EU_COLUMNS: ProductColumn[] = [
  {
    id: 'productId',
    label: 'Product ID',
    minWidth: 110,
    align: 'left',
  },
  {
    id: 'productIdType',
    label: 'Product ID Type',
    minWidth: 120,
    align: 'left',
  },
  {
    id: 'merchantProductId',
    label: 'Merchant ID (M-PID)',
    minWidth: 150,
    align: 'left',
  },
  {
    id: 'standardManufacturerProductId',
    label: 'Barcode (S-PID)',
    minWidth: 140,
    align: 'left',
  },
  {
    id: 'nonStandardManufacturerProductId',
    label: 'Manufacturer part no. (NS-PID)',
    minWidth: 170,
    align: 'left',
  },
];

const US_COLUMNS: ProductColumn[] = [
  {
    id: 'cpscBeingSold',
    label: 'Item Being Sold',
    minWidth: 120,
    align: 'left',
    render: ({ row }: ProductRenderParamsType) => {
      const c = row.cpscCompliance || (row as any).compliance?.us;
      if (!c || c.beingSold === undefined) {
        return (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            -
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
          {c.beingSold ? 'Yes' : 'No'}
        </Typography>
      );
    },
  },
  {
    id: 'cpscDisclaimCode',
    label: 'Disclaim Code',
    minWidth: 110,
    align: 'left',
    render: ({ row }: ProductRenderParamsType) => {
      const c = row.cpscCompliance || (row as any).compliance?.us;
      if (c && c.beingSold === false) {
        return (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {normalizeCpscDisclaimCode(c.disclaimCode)}
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
      );
    },
  },
  {
    id: 'cpscAdditionalContext',
    label: 'Additional Context',
    minWidth: 140,
    align: 'left',
    render: ({ row }: ProductRenderParamsType) => {
      const c = row.cpscCompliance || (row as any).compliance?.us;
      if (c && c.beingSold === false && c.additionalContext) {
        return (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {c.additionalContext}
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
      );
    },
  },
  {
    id: 'cpscCertificateFiled',
    label: 'Certificate Filed',
    minWidth: 130,
    align: 'left',
    render: ({ row }: ProductRenderParamsType) => {
      const c = row.cpscCompliance || (row as any).compliance?.us;
      if (c && c.beingSold === true) {
        return (
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
            {c.certificateFiled === undefined ? '-' : c.certificateFiled ? 'Yes' : 'No'}
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
      );
    },
  },
  {
    id: 'cpscProductIdRegistry',
    label: 'Product ID (Registry)',
    minWidth: 150,
    align: 'left',
    render: ({ row }: ProductRenderParamsType) => {
      const c = row.cpscCompliance || (row as any).compliance?.us;
      if (c && c.beingSold === true && c.certificateFiled === true && c.productIdRegistry) {
        return (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {c.productIdRegistry}
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
      );
    },
  },
  {
    id: 'cpscCertificateVersionId',
    label: 'Certificate Version ID',
    minWidth: 155,
    align: 'left',
    render: ({ row }: ProductRenderParamsType) => {
      const c = row.cpscCompliance || (row as any).compliance?.us;
      if (c && c.beingSold === true && c.certificateFiled === true && c.certificateVersionId) {
        return (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {c.certificateVersionId}
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
      );
    },
  },
  {
    id: 'cpscCertifierId',
    label: 'Certifier ID',
    minWidth: 120,
    align: 'left',
    render: ({ row }: ProductRenderParamsType) => {
      const c = row.cpscCompliance || (row as any).compliance?.us;
      if (c && c.beingSold === true && c.certificateFiled === true && c.certifierId) {
        return (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {c.certifierId}
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
      );
    },
  },
  {
    id: 'productId',
    label: 'Product ID',
    minWidth: 110,
    align: 'left',
    render: ({ row }: ProductRenderParamsType) => {
      const c = row.cpscCompliance || (row as any).compliance?.us;
      const pid = row.productId || c?.productId;
      if (pid) {
        return (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {pid}
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
      );
    },
  },
  {
    id: 'productIdType',
    label: 'Product ID Type',
    minWidth: 120,
    align: 'left',
    render: ({ row }: ProductRenderParamsType) => {
      const c = row.cpscCompliance || (row as any).compliance?.us;
      const ptype = row.productIdType || c?.productIdType || 'SKU';
      if (ptype) {
        return (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {ptype}
          </Typography>
        );
      }
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
      );
    },
  },
];

const TOTAL_VALUE_COLUMN: ProductColumn = {
  id: 'totalValue',
  label: 'Total Value',
  minWidth: 100,
  align: 'right',
  render: ({ row }: ProductRenderParamsType) => {
    const totalValue = Number(row.quantity) * (row.unitValue ?? 0);
    const formattedTotalValue = totalValue.toFixed(4);
    return (
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'right' }}>
        {row.unitValue ? formattedTotalValue : '0.00'}
      </Typography>
    );
  },
};

const ACTIONS_COLUMN: ProductColumn = {
  id: 'actions',
  label: 'Actions',
  minWidth: 90,
  align: 'center',
  render: ({ row, ind, handleEdit, handleDelete, selectedProduct }: ProductRenderParamsType) => {
    const isBeingEdited = selectedProduct?.id ? selectedProduct.id === ind.toString() : false;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleEdit && handleEdit({ ...row, id: ind.toString() })}
          disabled={isBeingEdited}
          sx={{ p: 0.75 }}
        >
          <ModeEditOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={() => handleDelete && handleDelete(ind)}
          disabled={isBeingEdited}
          sx={{ p: 0.75 }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    );
  },
};

interface PropsType {
  title?: string;
  products?: ProductType[];
  enableActions: boolean;
  handleEdit?: (data: ProductType) => void;
  handleDelete?: (ind: number) => void;
  selectedProduct?: ProductType | null;
  destinationCountry?: any;
  isEU?: boolean;
  isUS?: boolean;
}

export default function ProductTable({
  title,
  products,
  enableActions,
  handleEdit,
  handleDelete,
  selectedProduct,
  destinationCountry,
  isEU,
  isUS,
}: PropsType) {
  const theme = useTheme();

  const isDestEU = isEU !== undefined ? isEU : isEUCountry(destinationCountry);
  const isDestUS = isUS !== undefined ? isUS : isUSDestination(destinationCountry);

  const columnDefs = useMemo(() => {
    const list: ProductColumn[] = [...BASE_COLUMNS];
    if (isDestEU) {
      list.push(...EU_COLUMNS);
    } else if (isDestUS) {
      list.push(...US_COLUMNS);
    }
    list.push(TOTAL_VALUE_COLUMN);
    if (enableActions) {
      list.push(ACTIONS_COLUMN);
    }
    return list;
  }, [isDestEU, isDestUS, enableActions]);

  return (
    <Box sx={{ mt: 2 }}>
      {title && (
        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main', mb: 1 }}>
          {title} ({products?.length || 0})
        </Typography>
      )}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: '10px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <TableContainer sx={{ maxHeight: 380, overflow: 'auto', position: 'relative' }}>
          <Table stickyHeader size="small" aria-label="products table" sx={{ minWidth: 1320, tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    bgcolor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#F8F9FA',
                    color: '#344054',
                    borderBottom: '2px solid #E4E7EC',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    py: 1.25,
                    px: 1,
                    width: 50,
                    minWidth: 50,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  #
                </TableCell>
                {columnDefs.map(column => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      bgcolor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#F8F9FA',
                      color: '#344054',
                      borderBottom: '2px solid #E4E7EC',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      py: 1.25,
                      px: 1.5,
                      minWidth: column.minWidth || 100,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {products?.map((row, ind) => {
                return (
                  <TableRow
                    key={`product-${ind}`}
                    hover
                    sx={{
                      '& td': {
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                      },
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <TableCell sx={{ textAlign: 'center', py: 1, px: 1, width: 50, minWidth: 50 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {ind + 1}
                      </Typography>
                    </TableCell>
                    {columnDefs.map(column => {
                      const value = (row as any)[column.id];
                      const formattedValue = typeof value === 'number' && column?.format ? column.format(value) : value;
                      const displayValue =
                        formattedValue !== undefined && formattedValue !== null && String(formattedValue).trim() !== ''
                          ? formattedValue
                          : '-';
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align || 'left'}
                          sx={{
                            py: 1,
                            px: 1.5,
                            minWidth: column.minWidth || 100,
                            verticalAlign: 'middle',
                          }}
                        >
                          {column?.render ? (
                            column.render({ row, ind, handleEdit, handleDelete, selectedProduct })
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.primary',
                                fontSize: '0.85rem',
                                wordBreak: 'break-word',
                              }}
                            >
                              {displayValue}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
